import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { JSX } from 'react';
import root from 'react-shadow';
import { i18n, i18nMaybe } from 'shared/i18n';
import { useCss } from 'entities/style';
import { useSelectedTemplates } from 'entities/template';
import { useGetDocument, useGetDocumentTableOfContent } from 'entities/document';
import { useUser } from 'app/state/contexts';
import { HoveredTarget, buildHoverModifier } from '../utils/builtin-selectors';

const TOC_HIGHLIGHT_CSS = `
  .dc-hover-editor-table-of-content--content .editor-table-of-content--content,
  .dc-hover-toc-item .toc-item,
  .dc-hover-toc-header .toc-header,
  .dc-hover-toc-pagenumber .toc-pagenumber,
  .dc-hover-tag-ol ol,
  .dc-hover-tag-li li,
  .dc-hover-tag-a a {
    outline: 2px solid #547ba2;
    outline-offset: 1px;
  }
`;

interface PreviewHeader {
  name: string;
  pageNumber?: number;
  children?: PreviewHeader[];
}

const SAMPLE_HEADINGS: PreviewHeader[] = [
  { name: 'dc:preview.toc-sample-1', pageNumber: 1 },
  {
    name: 'dc:preview.toc-sample-2',
    pageNumber: 3,
    children: [
      { name: 'dc:preview.toc-sample-2a', pageNumber: 4 },
      { name: 'dc:preview.toc-sample-2b', pageNumber: 5 },
    ],
  },
  { name: 'dc:preview.toc-sample-3', pageNumber: 7 },
];

function renderEntry(entry: PreviewHeader, key: number, translateName: boolean): JSX.Element {
  const label = translateName ? i18nMaybe(entry.name) : entry.name;
  return (
    <li key={key}>
      <div className="toc-item">
        <a>
          <span className="toc-header">{label}</span>
          {entry.pageNumber != null && <span className="toc-pagenumber">{entry.pageNumber}</span>}
        </a>
      </div>
      {entry.children && entry.children.length > 0 && <ol>{entry.children.map((c, i) => renderEntry(c, i, translateName))}</ol>}
    </li>
  );
}

interface TocPreviewProps {
  hoveredInner?: HoveredTarget | null;
}

export function TocPreview({ hoveredInner }: TocPreviewProps = {}) {
  const { t } = useTranslation('common');

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { data: pData } = useGetDocument(documentId, selectedOrganization);
  const { data: selectedTemplates } = useSelectedTemplates(documentId);
  const css = useCss({ documentSuperId: documentId, documentRevisionId: undefined, useDraft: true, withStyle: undefined, embedd: undefined });
  const metadataTexts: Record<string, Record<string, string>> = pData?.content?.metadata?.texts ?? {};
  const previewLocale = Object.keys(metadataTexts)[0];
  const tocQuery = useGetDocumentTableOfContent(pData?.revisionId ?? null, { locale: previewLocale });

  if (css.isLoading || !css.data || !pData) {
    return (
      <div
        className="toc-preview text-muted fs-7 d-flex align-items-center justify-content-center"
        style={{ height: 140, border: '1px dashed #dee2e6', borderRadius: 4 }}
      >
        {t('loading')}
      </div>
    );
  }

  const templateSuperId = selectedTemplates?.[0] ?? '';
  const realHeaders = (tocQuery.data?.content as PreviewHeader[] | undefined) ?? undefined;
  const useReal = !!realHeaders && realHeaders.length > 0;
  const headers: PreviewHeader[] = realHeaders && realHeaders.length > 0 ? realHeaders : SAMPLE_HEADINGS;

  const hoverModifier = buildHoverModifier('editor-table-of-content--container', hoveredInner);

  return (
    <>
      {!useReal && <p className="text-muted fs-7 fst-italic mb-1">{i18n('dc:preview.toc-sample-notice')}</p>}
      <div className="toc-preview mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 4, maxHeight: '350px', overflowY: 'auto', overflowX: 'auto' }}>
        <root.div>
          <style>{css.data.shadowedCss}</style>
          <style>{TOC_HIGHLIGHT_CSS}</style>
          <div
            className={`page-content xrp-template-${templateSuperId}`}
            style={{ position: 'relative', width: '100%', height: 'auto', minHeight: 300, padding: '12px 20px', overflow: 'visible', boxSizing: 'border-box' }}
          >
            <div className={`editor-table-of-content--container ${hoverModifier}`.trim()}>
              <div className="editor-table-of-content--content">
                <ol>{headers.map((h, i) => renderEntry(h, i, !useReal))}</ol>
              </div>
            </div>
          </div>
        </root.div>
      </div>
    </>
  );
}
